import { Injectable } from '@angular/core';

/**
 * One-shot gate for the account-wide boot loads dispatched from {@link AccountManagementModule}'s
 * constructor.
 *
 * That module sits in both the eager import graph (AppModule → AccountCommonModule → SpacesModule)
 * and every lazy feature's graph (e.g. SpacetimeModule → Nav2Module/AccountCommonModule →
 * SpacesModule). Angular builds a module instance per injector, so the constructor runs again inside
 * each lazy chunk's injector and re-dispatches loads for data the store already holds. That second
 * pass is worse than redundant: `loadContextDetails` resets `details` to null on dispatch, so it
 * blanks loaded context and refetches it — while the lazy chunk is still evaluating.
 *
 * Dispatching once per document is safe for the space picker, which always reloads the document:
 * it assigns `location.href` (see SpacesComponent.swapUrl), so a space chosen there arrives with a
 * fresh root injector and a fresh instance of this service.
 *
 * The space-scoped loads are skipped when the document starts without a space, so a space entered
 * in-document from `/home?redirectTo=<space>` (see HomeRedirectComponent) runs without them.
 *
 * Provided in root, so every lazy injector resolves the same instance.
 */
@Injectable({ providedIn: 'root' })
export class AccountBootLoads {
    private dispatched = false;

    /** Runs `dispatch` the first time it is called for this document; a no-op after that. */
    dispatchOnce(dispatch: () => void): void {
        if (this.dispatched) {
            return;
        }
        this.dispatched = true;
        dispatch();
    }
}
